import * as NavigationMenu from "@radix-ui/react-navigation-menu";
import { type BlokProps, StoryblokComponent } from "@trydig/storyblok";
import type { ISbStoryData } from "storyblok";
import { BlokWrap } from "@/components/bloks/BlokWrap";
import { NavMenuContent } from "@/components/bloks/MegaMenuBlok/components/NavMenuContent";
import { NavMenuTrigger } from "@/components/bloks/MegaMenuBlok/components/NavMenuTrigger";
import { Asset } from "@/components/ui/Asset";
import { Container } from "@/components/ui/Container";
import type { ArticlePageStoryblok, MegaMenuStoryblok } from "@/generated/sb/sb-types";
import { Link } from "@/i18n/routing";
import { sbLink } from "@/lib/storyblok/link";

const MegaMenuStoryCard = ({ story }: { story: ISbStoryData<ArticlePageStoryblok> }) => (
	<NavigationMenu.Link asChild>
		<Link
			href={sbLink(story.full_slug)?.href ?? story.full_slug}
			className="group flex flex-col gap-2 w-full"
		>
			<div className="relative aspect-square xl:aspect-[160/213] overflow-hidden">
				<Asset asset={story.content.previewImage} sizes="220px" />
				<div
					aria-hidden
					className="absolute inset-0 bg-overlay-2 transition-colors duration-200 ease-out group-hover:bg-neutral-transparent-black-25"
				/>
			</div>
			<p className="font-display leading-[125%] text-[16px]">{story.content.previewTitle}</p>
		</Link>
	</NavigationMenu.Link>
);

export const MegaMenuBlok = ({ blok, preview }: BlokProps<MegaMenuStoryblok>) => {
	const href = sbLink(blok.link)?.href;
	const stories = (blok.stories ?? []) as ISbStoryData<ArticlePageStoryblok>[];
	return (
		<BlokWrap editable={blok} className="h-full flex items-center">
			<NavMenuTrigger
				asChild={!!href}
				className="font-display p-1-5 font-[300] leading-[125%] text-sm menu-trigger-link"
			>
				{href ? <Link href={href}>{blok.title}</Link> : blok.title}
			</NavMenuTrigger>
			<NavMenuContent className="bg-brand-sand-10">
				<Container
					as="div"
					className="h-full pt-8 pb-16 flex flex-col lg:flex-row lg:gap-(--layout-gap) items-start"
				>
					<div className="flex flex-wrap flex-1 min-w-0 gap-x-(--layout-gap) gap-y-8 items-start">
						{blok.sections?.map((section, i) => (
							<StoryblokComponent blok={section} key={i} preview={preview} index={i} />
						))}
					</div>
					<div className="flex shrink-0 flex-col xl:flex-row gap-(--layout-gap) w-55 xl:w-auto lg:ml-auto">
						{stories.slice(0, 1).map((story, i) => (
							<div key={story.uuid ?? i} className="w-55">
								<MegaMenuStoryCard story={story} />
							</div>
						))}
					</div>
				</Container>
			</NavMenuContent>
		</BlokWrap>
	);
};
